import { Box, BoxProps, Button, Icon } from '@dtx-company/shared-components/src'
import { HeapContainers } from '../../constants/heap'
import { Skeleton } from '@dtx-company/design-system/src'
import { StyledLi } from './styles'
import React from 'react'
import styled from 'styled-components'

const DEFAULT_ASSET_FILE = '/icons/flowcode-outline.svg'

interface DesignOption {
  id?: string
  name?: string
  assetFile?: {
    assetName: string
    url: string
  }
}

export interface DesignOptionsProps<T extends DesignOption> {
  options: T[]
  selectedOption: string | undefined
  onClick: (template: T) => void
  isPattern?: boolean
  noWrap?: boolean
  paddingBlockEnd?: number
  showSeperator?: boolean
}

const StyledUl = styled.ul<{ noWrap?: boolean; paddingBlockEnd?: number }>`
  display: flex;
  flex-wrap: ${({ noWrap }) => (noWrap ? 'nowrap' : 'wrap')};
  padding: 0;
  margin: 0;
  ${({ paddingBlockEnd }) => paddingBlockEnd && `padding-right: ${paddingBlockEnd}px;`}
`
const StyledButton = styled(Button)<{ selected?: boolean }>`
  :focus {
    border: ${({ selected, theme }) =>
      selected ? `4px solid ${theme.colors.primary.flowBlue}` : '4px solid grey'};
  }
`
export const DesignOptions = <T extends DesignOption>({
  options,
  selectedOption,
  onClick,
  noWrap = false,
  paddingBlockEnd,
  id,
  'aria-labelledby': ariaLabelledBy,
  loading,
  ...rest
}: DesignOptionsProps<T> & BoxProps & { loading?: boolean }): JSX.Element => {
  return (
    <Box {...rest}>
      <StyledUl
        noWrap={noWrap}
        paddingBlockEnd={paddingBlockEnd}
        role="radiogroup"
        aria-labelledby={ariaLabelledBy}
      >
        {loading &&
          Array(6)
            .fill(null)
            .map((_, i) => (
              <Skeleton width="48px" height="48px" variant="circular" key={i} sx={{ m: 1 }} />
            ))}
        {options.map((option, index) => {
          const isSelected = selectedOption === option.id
          return (
            <StyledLi key={option.id ?? index} role="radio" aria-checked={isSelected}>
              <StyledButton
                data-heap-id={`DesignOptions${index}_Button`}
                type="button"
                margin={'5px'}
                backgroundColor={isSelected ? '' : 'primary.white'}
                key={option.id}
                width="48px"
                height="48px"
                colorVariant="invisible"
                borderRadius={4}
                selected={isSelected}
                border={`${isSelected ? '3px' : '1px'} solid`}
                borderColor={isSelected ? 'primary.flowBlue' : 'secondary.border'}
                disabled={!option.id}
                onClick={() => onClick(option)}
                padding={0}
                id={
                  id ??
                  `hp-${HeapContainers.CREATE_FLOWCODE}-select_option_${(
                    option.assetFile?.assetName || option.id
                  )?.replace(' ', '_')}-btn`
                }
                data-testid={`btn-${option.name}`}
              >
                <Icon
                  src={option.assetFile?.url || DEFAULT_ASSET_FILE}
                  alt={`${option.assetFile?.assetName} pattern` || `Flowcode design ${index + 1}`}
                  width="30px"
                  height="30px"
                />
              </StyledButton>
            </StyledLi>
          )
        })}
      </StyledUl>
    </Box>
  )
}
